import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Tag } from "antd";
import { useGetIntegrationStepLogQuery } from "api/TenantSlug/StepLog/hooks";
// import { RunHistoryLink } from "components/CC/RunHistory/StepDetails/RunHistoryLink";
import { StepDetailRenderer } from "components/CC/RunHistory/StepDetails/StepDetailRenderer";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList";
import { QueryState } from "components/UI/QueryState/QueryState";
import { useStepTypeLabel } from "components/UI/Selects/StepTypeSelectSearchable/useStepTypeLabel";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { DrawerId } from "types/models";

import StepLogIntegrationData = App.Data.StepLogIntegrationData;

interface IntegrationStepLogDetailDrawerProps {
    logId: string;
}

export const IntegrationStepLogDetailDrawer: FC<IntegrationStepLogDetailDrawerProps> = ({ logId }) => {
    const { t } = usePaldockTranslation("drawers.integration-step-log-detail");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const { t: tCC } = usePaldockTranslation("components.connection-creator");
    const getStepTypeLabel = useStepTypeLabel();
    const drawerId = DrawerId.IntegrationStepLogDetail;

    const getIntegrationLogQuery = useGetIntegrationStepLogQuery(logId, {
        filter: { include: "offer,cascade,stepRun,owner,advertiser,conversionState" },
    });
    const log = getIntegrationLogQuery.data?.integrationLog;

    const logList = log
        ? PropertiesList.create<StepLogIntegrationData>(log, [
              {
                  term: tG("date"),
                  description: ({ created_at }) => (created_at ? <DateTimeCell value={created_at} /> : <EmptyCell />),
              },
              {
                  term: tG("origin_id"),
                  description: ({ origin_eid }) =>
                      origin_eid ? <ShortenedIdWithCopy charactersBetweenDots={35} id={origin_eid} /> : <EmptyCell />,
              },
              {
                  term: tG("event_id"),
                  description: ({ eid }) =>
                      eid ? <ShortenedIdWithCopy charactersBetweenDots={35} id={eid} /> : <EmptyCell />,
              },
              {
                  term: tG("affiliate"),
                  description: ({ owner }) => <EntityLinkCell.Owner entity={owner} />,
              },
              {
                  term: tG("advertiser"),
                  description: ({ advertiser }) => <EntityLinkCell.Advertiser entity={advertiser} />,
              },
              {
                  term: tG("offer"),
                  description: ({ offer }) => <EntityLinkCell.Offer entity={offer} />,
              },
              {
                  term: t("fields.channel"),
                  description: ({ cascade }) => <EntityLinkCell.Cascade entity={cascade} />,
              },
              {
                  term: t("fields.integration"),
                  description: ({ integration_id, integration_name }) =>
                      integration_id && integration_name ? (
                          <EntityLinkCell.Integration
                              entity={{ id: integration_id, name: integration_name } as App.Data.IntegrationData}
                          />
                      ) : (
                          <EmptyCell />
                      ),
              },
              {
                  term: t("fields.flow"),
                  description: ({ meaning }) => {
                      const meaningToFlowType: Record<string, string> = {
                          check: "ping",
                          send: "post",
                          postsend_verification: "postVerify",
                      };
                      return meaning ? tCC(`enums.flow_types.${meaningToFlowType[meaning] ?? meaning}`) : <EmptyCell />;
                  },
              },
              {
                  term: t("fields.step"),
                  description: ({ stepRun }) => stepRun?.title || <EmptyCell />,
              },
              {
                  term: t("fields.type"),
                  description: ({ stepRun }) =>
                      stepRun?.type ? (
                          stepRun.type === "integration" ? (
                              getStepTypeLabel(stepRun.type).split(" - ")[0]
                          ) : (
                              getStepTypeLabel(stepRun.type)
                          )
                      ) : (
                          <EmptyCell />
                      ),
              },
              {
                  term: tG("status"),
                  description: ({ http_status_code }) => http_status_code ?? <EmptyCell />,
              },
              {
                  term: tG("state"),
                  description: ({ state }) =>
                      state ? (
                          <Tag icon={<CheckCircleOutlined />} color="green">
                              {tGL("ok")}
                          </Tag>
                      ) : (
                          <Tag icon={<CloseCircleOutlined />} color="red">
                              {tGL("fail")}
                          </Tag>
                      ),
              },
          ])
        : null;

    return (
        <Drawer
            drawerId={drawerId}
            title={<DrawerTitle mainTitle={t("title")} />}
            searchParamsToClean={[`${drawerId}-id`]}
            // extraButtons={<RunHistoryLink />}
        >
            {!log ? (
                <QueryState
                    isLoading={getIntegrationLogQuery.isLoading}
                    isError={getIntegrationLogQuery.isError || (!getIntegrationLogQuery.isLoading && !log)}
                    errorKey="errors.something_went_wrong"
                />
            ) : (
                <div className="flex flex-col gap-4">
                    <DrawerContent className="flex flex-col gap-4 p-4">
                        <DrawerCollapsible title={t("sections.log")}>
                            <PropertiesList list={logList ?? []} />
                        </DrawerCollapsible>
                        {log.stepRun && (
                            <StepDetailRenderer
                                stepRun={log.stepRun}
                                logContext={{
                                    httpStatusCode: log.http_status_code,
                                    message: log.message,
                                    conversionState: log.conversionState,
                                }}
                            />
                        )}
                    </DrawerContent>
                </div>
            )}
        </Drawer>
    );
};
